import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface SegmentedControlOption<TValue extends string> {
  value: TValue;
  label: ReactNode;
  disabled?: boolean | undefined;
}

interface SegmentedControlProps<TValue extends string> {
  ariaLabel: string;
  value: TValue;
  options: readonly SegmentedControlOption<TValue>[];
  onValueChange: (value: TValue) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Outlined like the outline buttons and toolbar fields it sits next to; the selected segment takes
 * a quiet grey fill so it never competes with primary actions.
 */
export function SegmentedControl<TValue extends string>({
  ariaLabel,
  value,
  options,
  onValueChange,
  disabled = false,
  className,
}: SegmentedControlProps<TValue>) {
  return (
    <div
      className={cn(
        'inline-flex h-9 items-center gap-0.5 rounded-md border border-[var(--border-strong)] bg-[var(--bg-white)] p-[3px]',
        className,
      )}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled || option.disabled}
            aria-pressed={selected}
            className={cn(
              'focus-visible:ring-ring/40 ease-fluid h-full cursor-pointer rounded-[5px] px-3 text-xs font-medium whitespace-nowrap transition-[color,background-color] duration-[var(--duration-fluid)] focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground enabled:hover:bg-[var(--bg-page)]',
            )}
            onClick={() => {
              onValueChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
